"use client";

import React, { useState, useEffect, useRef } from "react";
import type { AgentInfo } from "./HighlightOverlay";
import { getCaretCoordinates } from "@/lib/textarea-caret";
import { ENDED_WORD } from "@/lib/team-state";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import { ZapIcon } from "@/components/icons/ZapIcon";
import { SearchIcon } from "@/components/icons/SearchIcon";
import { EditIcon } from "@/components/icons/EditIcon";
import { ChevronRightIcon } from "@/components/icons/ChevronRightIcon";

const DROPDOWN_DIVIDER_STYLE: React.CSSProperties = {
  borderBottom: "1px solid var(--mention-divider)",
};

/**
 * A lane's row wears the word §3.3 derived for it (`LaneWord.word`), and the
 * badge colours the THREE families that word can belong to. Told apart by the
 * CLOSED ended set, never by a hand list of live verbs: `FAMILY_DESK` grows a
 * verb whenever the story learns a new kind of step, and a list here would go
 * quietly grey the day it did.
 */
const ENDED_WORDS = new Set<string>(Object.values(ENDED_WORD));

function laneWordColor(word: string): string {
  // The one word that wants a person. Amber is what every other surface pages
  // with (the desk, the chip), so it means the same thing here.
  if (word === "waiting for you") return "var(--color-amber)";
  // A lane that is over has said all it is going to say: the badge goes quiet.
  if (ENDED_WORDS.has(word)) return "var(--mention-badge-text)";
  // Still out and still working — the brand's own live ink.
  return "var(--gold-ink)";
}

interface MentionDropdownProps {
  open?: boolean;
  agents: AgentInfo[];
  /** The element that has focus while the person types: the list hears its
   *  arrow / Enter / Tab keys, and measures its caret when it is a textarea. */
  anchorRef: React.RefObject<HTMLElement | null>;
  triggerPos?: number;
  /** For an editor that is NOT a textarea (the pill's rich editor): the caret's
   *  rect, already shaped for the popover (the editor's left and width, the
   *  caret's top and height; rich-composer/caret.ts). Absent, a textarea anchor
   *  is measured exactly as it always was. */
  caretRect?: () => DOMRect | null;
  zIndex?: string;
  onSelect: (agent: AgentInfo) => void;
  onClose: () => void;
}

export function MentionDropdown({
  open = true,
  agents,
  anchorRef,
  triggerPos,
  caretRect,
  zIndex = "",
  onSelect,
  onClose,
}: MentionDropdownProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset selection when agents change or dropdown opens
  useEffect(() => {
    setSelectedIndex(0);
  }, [agents, open]);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (open && listRef.current) {
      const selectedItem = listRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedIndex, open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open || !anchorRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // An Enter that COMMITS an IME composition is not a pick: with pinyin or
      // romaji after the trigger the letters being composed filter the list,
      // and picking there rewrites the editor under a live composition. The
      // legacy 229 covers engines that leave isComposing unset.
      if (e.isComposing || e.keyCode === 229) return;
      if (agents.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev < agents.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : agents.length - 1));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        onSelect(agents[selectedIndex]);
      }
    };

    const el = anchorRef.current;
    el.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      el.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open, agents, selectedIndex, anchorRef, onSelect]);

  return (
    <Popover
      open={open && agents.length > 0}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <PopoverAnchor
        virtualRef={{
          current: {
            getBoundingClientRect: () => {
              const fromHost = caretRect?.();
              if (fromHost) return fromHost;
              const anchor = anchorRef.current;
              if (
                anchor instanceof HTMLTextAreaElement &&
                typeof triggerPos === "number"
              ) {
                const caret = getCaretCoordinates(anchor, triggerPos);
                const textareaRect = anchor.getBoundingClientRect();
                return new DOMRect(
                  textareaRect.left,
                  caret.top,
                  textareaRect.width,
                  caret.height
                );
              }
              return anchor?.getBoundingClientRect() || new DOMRect(0, 0, 0, 0);
            },
          },
        }}
      />
      <PopoverContent
        className={`mention-dropdown w-(--radix-popover-trigger-width) ${zIndex} p-0`}
        align="start"
        side="bottom"
        sideOffset={8}
        role="listbox"
        aria-label="Agent suggestions"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div
          className="px-4 py-3 flex items-center gap-2"
          style={DROPDOWN_DIVIDER_STYLE}
        >
          <div className="w-5 h-5 rounded-8 flex items-center justify-center bg-fg-default">
            <span className="font-bold text-bg-base text-2.5">@</span>
          </div>
          <span
            className="text-3.5 font-medium tracking-wide uppercase"
            style={{ color: "var(--mention-header-text)" }}
          >
            Summon Agent
          </span>
        </div>

        {/* Agent List */}
        <div
          ref={listRef}
          className="py-2 max-h-64 overflow-y-auto overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {agents.map((agent, index) => {
            const isSelected = index === selectedIndex;
            const displayName = agent.slug;

            return (
              <button
                key={agent.id}
                data-index={index}
                onClick={() => onSelect(agent)}
                onMouseEnter={() => setSelectedIndex(index)}
                onPointerDown={(e) => e.preventDefault()}
                role="option"
                aria-selected={isSelected}
                className="relative overflow-hidden w-full text-left px-3 py-2.5 mx-1 rounded-16 transition-colors duration-150"
                style={{
                  width: "calc(100% - 8px)",
                  background: isSelected
                    ? "var(--mention-item-selected-bg)"
                    : "transparent",
                  border: isSelected
                    ? "1px solid var(--mention-item-selected-border)"
                    : "1px solid transparent",
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-16 flex items-center justify-center flex-shrink-0"
                    style={{
                      background:
                        agent.slug === "bash"
                          ? "var(--color-purple)"
                          : "var(--mention-icon-bg)",
                      border: "1px solid var(--mention-icon-border)",
                    }}
                  >
                    {agent.slug === "bash" ? (
                      <ZapIcon width={18} height={18} stroke="white" />
                    ) : agent.slug === "researcher" ? (
                      <SearchIcon
                        width={16}
                        height={16}
                        stroke="var(--mention-icon-stroke)"
                      />
                    ) : (
                      <EditIcon
                        width={16}
                        height={16}
                        stroke="var(--mention-icon-stroke)"
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-3.5 font-semibold"
                        style={{
                          color:
                            agent.slug === "bash"
                              ? "var(--fg-default)"
                              : "var(--mention-agent-name)",
                        }}
                      >
                        @{displayName}
                      </span>
                      <span
                        className="text-2.5 font-medium px-1.5 py-0.5 rounded-8 uppercase tracking-wider"
                        style={{
                          background:
                            agent.slug === "bash"
                              ? "color-mix(in srgb, var(--color-purple) 15%, var(--bg-elevated))"
                              : "var(--mention-badge-bg)",
                          color:
                            agent.slug === "bash"
                              ? "var(--color-purple)"
                              : "var(--mention-badge-text)",
                        }}
                      >
                        {agent.category}
                      </span>
                      {/* A lane entry carries its own state word (§3.3) in
                        `description`; surface it as the state badge. R38: a
                        helper is a lane too, so both categories keep it. */}
                      {(agent.category === "teammate" ||
                        agent.category === "helper") && (
                        <span
                          className="text-2.5 font-medium px-1.5 py-0.5 rounded-8 uppercase tracking-wider"
                          style={{
                            background: "var(--mention-badge-bg)",
                            color: laneWordColor(agent.description),
                          }}
                        >
                          {agent.description}
                        </span>
                      )}
                    </div>
                    <p
                      className="text-3.5 mt-0.5 truncate"
                      style={{ color: "var(--mention-description)" }}
                    >
                      {agent.slug === "bash"
                        ? "Primary orchestrator - Routes to specialists"
                        : agent.category === "teammate"
                          ? "Live teammate on this task"
                          : agent.category === "helper"
                            ? "Background helper on this task"
                            : agent.description}
                    </p>
                  </div>

                  {isSelected && (
                    <div className="w-6 h-6 rounded-12 flex items-center justify-center flex-shrink-0 bg-bg-muted">
                      <ChevronRightIcon
                        width={12}
                        height={12}
                        stroke="var(--fg-default)"
                        strokeWidth="3"
                      />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer Hint */}
        <div
          className="px-4 py-2.5 hidden can-hover:flex items-center justify-between"
          style={{
            borderTop: "1px solid var(--mention-divider)",
            background: "var(--mention-footer-bg)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <kbd
                className="px-1.5 py-0.5 rounded text-2.5 font-mono text-4"
                style={{
                  background: "var(--mention-kbd-bg)",
                  color: "var(--mention-kbd-text)",
                }}
              >
                up
              </kbd>
              <kbd
                className="px-1.5 py-0.5 rounded text-2.5 font-mono text-4"
                style={{
                  background: "var(--mention-kbd-bg)",
                  color: "var(--mention-kbd-text)",
                }}
              >
                down
              </kbd>
              <span
                className="text-2.5 ml-1"
                style={{ color: "var(--mention-hint-text)" }}
              >
                navigate
              </span>
            </div>
            <div className="flex items-center gap-1">
              <kbd
                className="px-1.5 py-0.5 rounded text-2.5 font-mono text-4"
                style={{
                  background: "var(--mention-kbd-bg)",
                  color: "var(--mention-kbd-text)",
                }}
              >
                enter
              </kbd>
              <span
                className="text-2.5 ml-1"
                style={{ color: "var(--mention-hint-text)" }}
              >
                select
              </span>
            </div>
          </div>
          <span
            className="text-2.5"
            style={{ color: "var(--mention-hint-muted)" }}
          >
            esc to close
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
